// @flow
import Vue from 'vue'
import smoothScroll from 'smoothscroll'
import { buildComment } from 'app/utils/comments'
import { HEADER_HEIGHT } from 'app/constants'
import { fbAPIGet, fbAPIPostMulti } from 'app/utils/http-api'

type LikeOptions = { id: number, isLiked: boolean }

const scrollToOffset = HEADER_HEIGHT + 20

export default {
  focusForm({ commit }: CommentsActionContext, id: number = -1) {
    const selector = id <= 0 ? 'comment-form' : `comment-form-${id}`

    id && commit('openCommentForm', id)
    Vue.nextTick(() => {
      const commentForm = document.getElementById(selector)
      if (!commentForm) {
        return
      }
      smoothScroll(commentForm.offsetTop - scrollToOffset, 500, () => {
        const textarea = commentForm.querySelector('textarea')
        if (!textarea) {
          return
        }
        textarea.focus()
      })
    })
  },

  openFlagModal({ commit }: CommentsActionContext, id: number = -1) {
    commit('openFlagModal', id)
    commit('toggleCommentOptions')
  },
  openDeleteConfirmationModal({ commit }: CommentsActionContext, id: number = -1) {
    commit('openDeleteConfirmationModal', id)
    commit('toggleCommentOptions')
  },

  // called on page load if getting just 1 comment to show
  getInitialComment({ commit, dispatch }: CommentsActionContext, opts: Object) {
    const { commentId, url } = opts
    const getUrl = url || `comment/${commentId}`

    commit('setCommentsLoading', { status: true })

    fbAPIGet(
      getUrl,
      res => {
        dispatch('hideLoading')
        if (!res.body.data) {
          // should not get here, should get [] if none
          return
        }
        commit('replaceComments', res.body.data.map(buildComment))
      },
      () => {
        dispatch('hideLoading')
      },
    )
  },

  // called on page load and when changing sort
  getInitialComments({ commit, state, dispatch }: CommentsActionContext, opts: Object) {
    const { url, sort = state.commentSort, offset = 0 } = opts
    const getUrl =
      url ||
      `comments/${state.commentableType}/${state.commentableId}?sort=${sort}&offset=${offset}`

    commit('setCommentsLoading', { status: true })

    fbAPIGet(
      getUrl,
      res => {
        dispatch('hideLoading')
        if (!res.body.data) {
          // should not get here, should get [] if none
          return
        }
        commit('replaceComments', res.body.data.map(buildComment))
      },
      () => {
        dispatch('hideLoading')
      },
    )
  },

  sortAndReplaceComments({ commit, dispatch }: CommentsActionContext, sort: string) {
    commit('setCommentSort', sort)
    dispatch('getInitialComments', {})
  },

  /**
   * Adding a new parent comment or child / reply comment
   */
  submitComment(
    { commit, state, dispatch, rootState }: CommentsActionContext,
    { body, imageFile, imageDataString }: Object,
  ) {
    const { commentForm, commentableType, commentableId } = state
    const { user } = rootState
    const parentId = commentForm.id

    commit('closeCommentForm')
    commit('setCommentsLoading', { parent: parentId })
    commit('increaseCommentsTotal')

    // scroll to the loader where the comment will show up
    Vue.nextTick(() => {
      const placeholder = document.getElementById('comment-loader')
      if (placeholder) {
        smoothScroll(placeholder.offsetTop - scrollToOffset, 500, () => {})
      }
    })

    const formData = new window.FormData()
    formData.append('commentable_type', commentableType)
    formData.append('commentable_id', commentableId)
    formData.append('comment', body)
    if (parentId > 0) {
      formData.append('parent_id', parentId)
    }
    if (imageFile) {
      formData.append('image', imageFile)
    }

    fbAPIPostMulti(
      `comments`,
      formData,
      res => {
        const comment = res.body.data

        let newComment

        // old API endpoint returned only commentId, new returns entire comment
        // support both styles
        if (typeof comment === 'number') {
          newComment = buildComment({
            comment: body,
            id: comment, // actually the comment id
            user: user,
            imageDataString: imageDataString,
          })
        } else {
          // no data string, uses data / image from response
          newComment = buildComment(comment)
        }

        commit('addComment', newComment)
        dispatch('hideLoading')
      },
      () => {
        commit('decreaseCommentsTotal')
        dispatch('hideLoading')
      },
    )
  },

  /**
   * Editing a comment
   */
  async submitEditComment(
    { commit, state, dispatch }: CommentsActionContext,
    { id, parentId, body, imageFile, removeImage }: EditCommentPayload,
  ) {
    // switch to json in the future
    const formData = new window.FormData()
    formData.append('comment_id', id)
    formData.append('comment', body || '')
    formData.append('image_remove', removeImage || 0)

    if (imageFile) {
      formData.append('image', imageFile)
    }

    // XHR
    return new Promise((resolve, reject) => {
      fbAPIPostMulti(
        `comments/${id}`,
        formData,
        res => {
          commit('editComment', {
            ...res.body.data,
            parentId,
          })
          resolve()
        },
        err => {
          // error message
          console.warn('error processing comment edit form', err)
          reject(err)
        },
      )
    })
  },

  hideLoading({ commit }: CommentsActionContext) {
    commit('setCommentsLoading', { amount: 0, status: false })
  },

  setCommentLike({ commit }: ActionContext, opts: LikeOptions) {
    const { id, isLiked } = opts

    // note: not sure why we are toggling and not setting since we know isLiked state
    id && commit('toggleCommentLike', id)

    isLiked ? commit('decreaseCommentLikeCount', id) : commit('increaseCommentLikeCount', id)
  },
}
